import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useIsMounted } from 'usehooks-ts';
import Typography from '@lib/components/Typography/Typography';
import useWindowScroll, { Direction } from '@lib/hooks/useWindowScroll';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { ScannerActionType } from 'routes/Scanner/enums';
import styles from './ScanQrStickyBtn.module.scss';

type Props = {
  scannerActionType: ScannerActionType;
};

export default function ScanQrStickyBtn({ scannerActionType }: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const { t } = useTranslation();
  const { openScanner }: ContextProps = useOutletContext();
  const onOpenScanner = () => openScanner(scannerActionType);
  const { isScrolling, direction } = useWindowScroll();
  const isMounted = useIsMounted();
  return (
    <div className={styles.root}>
      <button
        type="button"
        className={classNames(styles.button, {
          [styles.buttonVisible]: isMounted(),
          [styles.buttonCollapsed]: isScrolling,
          [styles.buttonWhenUp]: direction === Direction.Up,
          [styles.buttonWhenDown]: direction === Direction.Down,
        })}
        onClick={onOpenScanner}
      >
        <svg
          ref={svgRef}
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7 1H4C2.34315 1 1 2.34315 1 4V7H3L3 4C3 3.44772 3.44772 3 4 3H7V1Z"
            fill="currentColor"
          >
            <animateTransform
              attributeName="transform"
              attributeType="XML"
              type="translate"
              values="0 0; -1 -1; 0 0"
              dur="1s"
              begin="0s; animate-top-left.end+10s"
              repeatCount="1"
              id="animate-top-left"
            />
          </path>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M1 17V20C1 21.6569 2.34315 23 4 23H7V21H4C3.44772 21 3 20.5523 3 20V17H1Z"
            fill="currentColor"
          >
            <animateTransform
              attributeName="transform"
              attributeType="XML"
              type="translate"
              values="0 0; -1 1; 0 0"
              dur="1s"
              begin="0s; animate-bottom-left.end+10s"
              repeatCount="1"
              id="animate-bottom-left"
            />
          </path>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M23 20V17H21V20C21 20.5523 20.5523 21 20 21H17V23H20C21.6569 23 23 21.6569 23 20Z"
            fill="currentColor"
          >
            <animateTransform
              attributeName="transform"
              attributeType="XML"
              type="translate"
              values="0 0; 1 1; 0 0"
              dur="1s"
              begin="0s; animate-bottom-right.end+10s"
              repeatCount="1"
              id="animate-bottom-right"
            />
          </path>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17 1H20C21.6569 1 23 2.34315 23 4V7H21V4C21 3.44772 20.5523 3 20 3H17V1Z"
            fill="currentColor"
          >
            <animateTransform
              attributeName="transform"
              attributeType="XML"
              type="translate"
              values="0 0; 1 -1; 0 0"
              dur="1s"
              begin="0s; animate-top-right.end+10s"
              repeatCount="1"
              id="animate-top-right"
            />
          </path>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.0858 5.5H13.9142L15.4142 7H17C18.1046 7 19 7.89543 19 9V16C19 17.1046 18.1046 18 17 18H7C5.89543 18 5 17.1046 5 16V9C5 7.89543 5.89543 7 7 7H8.58579L10.0858 5.5ZM10.9142 7.5L9.41421 9H7V16H17V9H14.5858L13.0858 7.5H10.9142Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12Z"
            fill="currentColor"
          />
        </svg>
        <Typography variant="body" className={styles.label}>
          {t('scan-qr')}
        </Typography>
      </button>
    </div>
  );
}
